import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Typography, visuallyHidden } from 'bonfire-kindling';
import { Redirect } from 'react-router-dom';
import { withRouter } from 'react-router';
import { useTranslation } from 'react-i18next';
import _debounce from 'lodash/debounce';
import axios from 'helpers/axios';
import { VENDORS_ME, PROJECTS_TYPES } from 'constants/apis';
import { canadianProvincesWithRegion, americanStatesWithRegion } from 'constants/regions';
import OpportunitiesTabs from './_Tabs/OpportunitiesTabs';
import OpportunitiesList from './_List/OpportunitiesList';
import OpportunitiesSearchBelt from './_Search/OpportunitiesSearchBelt';
import { useIframeLogin } from '../hooks/useIframeLogin';
import useSearchTab from './hooks/useSearchTab';
import { SORT_BY, PROJECT_STATUS, PROJECT_SOURCE } from 'constants/opportunities';
import useQueryParams from 'helpers/hooks/useQueryParams';
import { regionDataToLocations } from 'helpers/locations';

const allLocations = [...canadianProvincesWithRegion, ...americanStatesWithRegion].map(
  ({ value }) => value
);
const NUM_OF_TOTAL_LOCATIONS = allLocations.length;

const Opportunities = ({ match, location }) => {
  const { t } = useTranslation();
  const query = useQueryParams();
  const activeTab = (location.pathname || '').split('/').pop();
  const { logInToOrganizationDomain, handleLoadGoToProjectIframe, iframeRef, iframeSrc } =
    useIframeLogin();
  const [search, setSearch] = useState(query.get('search') || '');
  const [locations, setLocations] = useState(allLocations);
  const [vendorLocations, setVendorLocations] = useState([]);
  const [sortBy, setSortBy] = useState(SORT_BY.mostRecent);
  const [statuses, setStatuses] = useState(PROJECT_STATUS.open);
  const [sources, setSources] = useState(PROJECT_SOURCE.allSources);
  const [organizations, setOrganizations] = useState([]);
  const [types, setTypes] = useState({ available: [], selected: [], loaded: false });
  const [isVendorLoaded, setIsVendorLoaded] = useState(false);
  const searchTabProps = {
    search,
    sortBy,
    locations,
    organizations,
    statuses,
    sources,
    types: types.selected,
  };
  const recommended = useSearchTab('recommended', searchTabProps);
  const following = useSearchTab('following', searchTabProps);
  const all = useSearchTab('all', searchTabProps);

  const { triggerSearch: searchRecommended, updateFollowing: updateRecommendedFollowing } =
    recommended;
  const { triggerSearch: searchFollowing, updateFollowing } = following;
  const { triggerSearch: searchAll, updateFollowing: updateAllFollowing } = all;

  const triggerSearch = useCallback(() => {
    searchRecommended();
    searchFollowing();
    searchAll();
  }, [searchRecommended, searchFollowing, searchAll]);

  const triggerSearchDebounce = useMemo(() => _debounce(triggerSearch, 500), [triggerSearch]);

  useEffect(() => {
    (async () => {
      const [{ data: vendor }, { data: types }] = await Promise.all([
        axios.get(VENDORS_ME),
        axios.get(PROJECTS_TYPES),
      ]);
      setTypes({ available: types, selected: types, loaded: true });
      setVendorLocations(regionDataToLocations(vendor.regionData));
      setIsVendorLoaded(true);
    })();
  }, []);

  // Refetch when switching to this tab to incorporate new/removed follows
  useEffect(() => {
    if (activeTab === 'following') {
      searchFollowing();
    }
  }, [activeTab, searchFollowing]);

  useEffect(() => {
    if (isVendorLoaded && types.loaded) {
      triggerSearchDebounce();
    }
  }, [
    locations,
    organizations,
    statuses,
    sources,
    types.selected,
    types.loaded,
    sortBy,
    isVendorLoaded,
    triggerSearchDebounce,
  ]);

  useEffect(() => () => triggerSearchDebounce.cancel(), [triggerSearchDebounce]);

  const updateProjectFollowingStatus = useCallback(
    (projectUUID, following) => {
      updateRecommendedFollowing(projectUUID, following);
      updateFollowing(projectUUID, following);
      updateAllFollowing(projectUUID, following);
    },
    [updateAllFollowing, updateFollowing, updateRecommendedFollowing]
  );

  const onFilterChange = useCallback(({ locations, organizations, statuses, sources, types }) => {
    if (locations) setLocations(locations);
    if (locations === null) setLocations(allLocations);
    if (sources) setSources(sources);
    if (organizations) setOrganizations(organizations);
    if (types) setTypes(prev => ({ ...prev, selected: types }));

    if (statuses) {
      setStatuses(statuses);
      // Since we remove the "Closing Soon" option when filtering by closed
      // projects, the sort column also needs to be updated.
      if (statuses === PROJECT_STATUS.closed) {
        setSortBy(SORT_BY.closingSoon);
      }
    }
  }, []);

  const onSortChange = useCallback(e => {
    setSortBy(e.target.value);
  }, []);

  const onSearchChange = useCallback(
    e => {
      setSearch(e.target.value);

      triggerSearchDebounce();
    },
    [triggerSearchDebounce]
  );

  // This checks if the user has changed the filter from the initial default state.
  const hasUserChangedFilter = useMemo(() => {
    if (statuses !== PROJECT_STATUS.open) {
      return true;
    }

    if (types.selected.length !== types.available.length) {
      return true;
    }

    if (sources !== PROJECT_SOURCE.allSources) {
      return true;
    }

    if (location.pathname === '/opportunities/recommended') {
      const isFilteringRecommended = vendorLocations.length !== locations.length;
      return isFilteringRecommended;
    }

    const isFilteringFollowingOrAll = locations.length !== NUM_OF_TOTAL_LOCATIONS;
    return isFilteringFollowingOrAll;
  }, [
    statuses,
    types.selected.length,
    types.available.length,
    sources,
    location.pathname,
    locations.length,
    vendorLocations.length,
  ]);

  const vendorLocationsSet = useMemo(() => new Set(vendorLocations), [vendorLocations]);

  const effectiveLocations = useMemo(() => {
    const isValidVendorLocation = location => vendorLocationsSet.has(location);

    // If the user navigates to either the 'Following' or 'All' tabs and selects
    // locations outside of their vendor locations and then navigates
    // back to 'Recommended', this causes those locations to still appear in
    // the filter when they shouldn't. In that case, just reset the filter
    // back to the vendor locations and preserve any valid selections.
    if (activeTab === 'recommended' && !locations.every(isValidVendorLocation)) {
      return locations.filter(isValidVendorLocation);
    }

    return locations;
  }, [activeTab, locations, vendorLocationsSet]);

  let collection = null;
  switch (activeTab) {
    case 'recommended':
      collection = recommended;
      break;
    case 'following':
      collection = following;
      break;
    case 'all':
      collection = all;
      break;
    default:
      break;
  }
  if (match.isExact || !collection) {
    return <Redirect to={{ ...location, pathname: '/opportunities/recommended' }} />;
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        sx={{ minHeight: '100%', bgcolor: 'background.main', width: '100%' }}
      >
        <Typography component="h1" style={visuallyHidden}>
          {t('opportunities.screen_reader.opportunities')}
        </Typography>
        <Box display="flex" mt={4} mb={2} mx={2}>
          <OpportunitiesTabs
            activeTab={activeTab}
            totalRecommended={recommended.total}
            totalFollowing={following.total}
            total={all.total}
          />
        </Box>
        <Box m={2}>
          <OpportunitiesSearchBelt
            sortBy={sortBy}
            statuses={statuses}
            types={types}
            sources={sources}
            searchKeyword={search}
            locations={effectiveLocations}
            organizations={organizations}
            availableLocations={activeTab === 'recommended' ? vendorLocations : allLocations}
            onSortChange={onSortChange}
            onSearchChange={onSearchChange}
            onFilterChange={onFilterChange}
          />
        </Box>
        <OpportunitiesList
          collection={collection}
          fetchMore={collection.fetchMore}
          hasUserChangedFilter={hasUserChangedFilter}
          updateProjectFollowingStatus={updateProjectFollowingStatus}
          searchKeyword={search}
          logInToOrganizationDomain={logInToOrganizationDomain}
        />
      </Box>
      {iframeSrc && (
        <iframe
          style={{ display: 'none' }}
          ref={iframeRef}
          title={t('opportunities.project.go_to_project')}
          src={iframeSrc}
          onLoad={handleLoadGoToProjectIframe}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default withRouter(Opportunities);
